import { useQuery } from '@tanstack/react-query';
import { useHybridAuth } from './useHybridAuth';
import { useDriverStateV2 } from './useDriverStateV2';
import env from '../config/env';
import { getStoredSelectedCompanyId } from './userCompanies.shared';

export interface AccountBreakdown {
  accountId: string;
  accountName: string;
  callCount: number;
  totalInvoiced: number;
  totalPaid: number;
}

export interface CompanyStats {
  // Call statistics
  totalCalls: number;
  completedCalls: number;
  accountCalls: number;

  // Invoice statistics
  totalInvoiced: number;
  totalPaid: number;

  // Performance metrics
  averageCompletionTimeMinutes: number | null;

  // Account breakdown
  accountBreakdown: AccountBreakdown[];

  // Timestamp
  period: {
    startDate: string;
    endDate: string;
  };
}

export interface UseCompanyStatsOptions {
  startDate?: string;
  endDate?: string;
  accountId?: string;
}

/** pg / JSON may return numeric columns as strings; coerce for UI math and Intl. */
function toFiniteNumber(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

function toFiniteNumberOrNull(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function normalizeCompanyStats(raw: unknown): CompanyStats {
  const d = raw as Record<string, unknown>;
  const breakdown = Array.isArray(d.accountBreakdown)
    ? (d.accountBreakdown as Record<string, unknown>[]).map((a) => ({
        accountId: String(a.accountId ?? ''),
        accountName: String(a.accountName ?? ''),
        callCount: toFiniteNumber(a.callCount),
        totalInvoiced: toFiniteNumber(a.totalInvoiced),
        totalPaid: toFiniteNumber(a.totalPaid),
      }))
    : [];

  const period =
    d.period && typeof d.period === 'object' && d.period !== null
      ? (d.period as Record<string, unknown>)
      : {};

  return {
    totalCalls: toFiniteNumber(d.totalCalls),
    completedCalls: toFiniteNumber(d.completedCalls),
    accountCalls: toFiniteNumber(d.accountCalls),
    totalInvoiced: toFiniteNumber(d.totalInvoiced),
    totalPaid: toFiniteNumber(d.totalPaid),
    averageCompletionTimeMinutes: toFiniteNumberOrNull(
      d.averageCompletionTimeMinutes
    ),
    accountBreakdown: breakdown,
    period: {
      startDate: String(period.startDate ?? ''),
      endDate: String(period.endDate ?? ''),
    },
  };
}

const fetchCompanyAnalytics = async (
  token: string,
  companyId: string,
  options?: UseCompanyStatsOptions
): Promise<CompanyStats> => {
  try {
    // Build query parameters
    const params = new URLSearchParams();
    if (options?.startDate) {
      params.append('startDate', options.startDate);
    }
    if (options?.endDate) {
      params.append('endDate', options.endDate);
    }
    if (options?.accountId) {
      params.append('accountId', options.accountId);
    }

    const queryString = params.toString();
    const url = `${env.apiUrl}/calls/analytics${queryString ? `?${queryString}` : ''}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-company-id': companyId,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch analytics: ${response.statusText}`);
    }

    const data = await response.json();
    return normalizeCompanyStats(data);
  } catch (error: any) {
    console.error('Failed to fetch company analytics:', error);
    throw new Error(
      error.response?.data?.message || 'Failed to fetch analytics'
    );
  }
};

/**
 * Hook to fetch company analytics statistics from the backend
 * Used by the Analytics tab
 *
 * @param options - Optional filtering parameters
 * @param options.startDate - Start date in ISO format (YYYY-MM-DD)
 * @param options.endDate - End date in ISO format (YYYY-MM-DD)
 * @param options.accountId - Filter by specific customer account ID
 */
export const useCompanyStats = (options?: UseCompanyStatsOptions) => {
  const { getAccessTokenSilently } = useHybridAuth();
  const { driver } = useDriverStateV2();
  // An office member with no Driver row has no driver company; the stored
  // selection stands in, as it does for every other request.
  const companyId = driver?.companyId || getStoredSelectedCompanyId();

  return useQuery({
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      if (!companyId) {
        throw new Error('Company ID not available');
      }
      return fetchCompanyAnalytics(token, companyId, options);
    },
    queryKey: [
      'company-stats',
      companyId,
      options?.startDate,
      options?.endDate,
      options?.accountId,
    ],
    enabled: !!companyId,
    staleTime: 60 * 1000, // 1 minute
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });
};
